import { FacilityFlowCoreModulesPlanningEntitiesTimeClockEventType as TimeClockEventTypeEnum } from '@/api/generated/models';
import type { FacilityFlowCoreModulesPlanningWorkflowsTimeClockTimeClockDayDto } from '@/api/generated/models';
import { getClockState, sortEventsByTime } from '@/store/useTimeclockStore';
import { parseUtcTimestamp } from '@/utils/dateUtils';

type TimeClockDay = FacilityFlowCoreModulesPlanningWorkflowsTimeClockTimeClockDayDto;

const MINUTE_MS = 60_000;

export interface DayMinutes {
  workMinutes: number;
  breakMinutes: number;
}

/**
 * Minutes since the epoch of a counted time, floored to the minute. `effectiveTimestamp` is
 * already the counted time (not before the work count start „Zählbeginn“, FACF-87/149); the raw stamp is only a fallback.
 */
function countedMinute(event: { timestamp?: string; effectiveTimestamp?: string }): number | null {
  const date = parseUtcTimestamp(event.effectiveTimestamp ?? event.timestamp);
  return date ? Math.floor(date.getTime() / MINUTE_MS) : null;
}

/**
 * Counts a day the way the backend's TimeClockDayCalculator does: work runs from clock-in or
 * break end to the next event, breaks and clocked-out gaps between two shifts count as break.
 * A running work segment or break is counted up to `now` (floored, never before its start); a
 * trailing clock-out ends the day.
 */
export function computeDayMinutes(entry: TimeClockDay | null | undefined, now: Date): DayMinutes {
  const totals: DayMinutes = { workMinutes: 0, breakMinutes: 0 };
  let open: { kind: 'work' | 'break' | 'gap'; start: number } | null = null;

  for (const event of sortEventsByTime(entry?.events)) {
    const minute = countedMinute(event);
    if (minute === null) continue;
    if (open) addPiece(totals, open.kind, minute - open.start);

    switch (event.eventType) {
      case TimeClockEventTypeEnum.NUMBER_1:
      case TimeClockEventTypeEnum.NUMBER_4:
        open = { kind: 'work', start: minute };
        break;
      case TimeClockEventTypeEnum.NUMBER_3:
        open = { kind: 'break', start: minute };
        break;
      case TimeClockEventTypeEnum.NUMBER_2:
        open = { kind: 'gap', start: minute };
        break;
    }
  }

  if (open && open.kind !== 'gap') {
    const nowMinute = Math.floor(now.getTime() / MINUTE_MS);
    addPiece(totals, open.kind, Math.max(open.start, nowMinute) - open.start);
  }
  return totals;
}

function addPiece(totals: DayMinutes, kind: 'work' | 'break' | 'gap', minutes: number) {
  if (minutes <= 0) return;
  if (kind === 'work') totals.workMinutes += minutes;
  else totals.breakMinutes += minutes;
}

/** Worked minutes of a day, a running segment counted up to `now`. */
export function computeWorkedMinutes(entry: TimeClockDay | null | undefined, now: Date): number {
  return computeDayMinutes(entry, now).workMinutes;
}

/**
 * Work and break minutes to show for a day: live from the events while the day is still
 * running (clocked in or on a break), otherwise the server's totals.
 */
export function displayDayMinutes(entry: TimeClockDay | null | undefined, now: Date): DayMinutes {
  const state = getClockState(entry);
  if (state === 'in' || state === 'break') return computeDayMinutes(entry, now);
  return {
    workMinutes: parseTimeSpan(entry?.totalWorkTime) ?? 0,
    breakMinutes: parseTimeSpan(entry?.totalBreakTime) ?? 0,
  };
}

/**
 * Whole minutes of a .NET TimeSpan string ("07:30:00", "1.02:03:04", "-00:15:00"). Seconds and
 * fractions are dropped. Returns null for a missing or malformed value.
 */
export function parseTimeSpan(value: string | null | undefined): number | null {
  if (!value) return null;
  const match = /^(-)?(?:(\d+)\.)?(\d+):(\d{2})(?::(\d{2})(?:\.\d+)?)?$/.exec(value.trim());
  if (!match) return null;
  const [, sign, days, hours, minutes] = match;
  const total = Number(days ?? 0) * 24 * 60 + Number(hours) * 60 + Number(minutes);
  return sign ? -total : total;
}

/** Minutes as "h:mm" — hours are not wrapped at 24 ("26:03"). */
export function formatDuration(minutes: number): string {
  const sign = minutes < 0 ? '-' : '';
  const abs = Math.abs(Math.trunc(minutes));
  const hours = Math.floor(abs / 60);
  const mins = abs % 60;
  return `${sign}${hours}:${String(mins).padStart(2, '0')}`;
}

/** A TimeSpan string as "h:mm", or "–" when it is missing or malformed. */
export function formatTimeSpan(value: string | null | undefined): string {
  const minutes = parseTimeSpan(value);
  return minutes === null ? '–' : formatDuration(minutes);
}
